import { addDays, startOfDay } from "date-fns";

import { getDefaultHotelFormValue } from "@/feature/hotel/search/lib/hotel-search-defaults";
import type { IHotelsSearchSchema } from "@/feature/hotel/search/types/hotel-search-type";

/**
 * Check whether a hotel location object has the minimum required fields.
 */
export function isLocationValid(
  loc: IHotelsSearchSchema["location"],
): boolean {
  return Boolean(loc?.code && loc?.name);
}

/**
 * Sanitize search data so the form always has a usable state.
 * Invalid fields are silently replaced with sensible defaults.
 */
export function sanitizeSearchData(
  data: IHotelsSearchSchema,
): IHotelsSearchSchema {
  const defaults = getDefaultHotelFormValue();
  const today = startOfDay(new Date());

  // --- Location ---
  const location = isLocationValid(data.location)
    ? data.location
    : defaults.location;

  // --- Dates ---
  let from = new Date(data.date.from);
  let to = new Date(data.date.to);

  // If check-in is invalid or in the past → reset both
  if (Number.isNaN(from.getTime()) || from < today) {
    from = addDays(today, 3);
    to = addDays(today, 6);
  }

  // If check-out is invalid or not after check-in → push it
  if (Number.isNaN(to.getTime()) || to <= from) {
    to = addDays(from, 3);
  }

  // --- Rooms ---
  let rooms = data.rooms;
  if (!Array.isArray(rooms) || rooms.length === 0) {
    rooms = defaults.rooms;
  } else {
    rooms = rooms.map((room) => ({
      adults: room.adults >= 1 ? room.adults : 1,
      children: room.children >= 0 ? room.children : 0,
      infants: room.infants >= 0 ? room.infants : 0,
      children_ages: room.children_ages ?? [],
    }));
  }

  return { location, date: { from, to }, rooms };
}
